import { useState } from 'react';
import { Alert, Box, Button, Code, Group, Modal, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { Company } from 'src/secret-menu/hooks/useCompanies';
import PageLoader from 'src/UI/loader/PageLoader';
import BotImageUpload from './components/BotImageUpload';
import BulletStylesForm from './components/BulletStylesForm';
import ColorPaletteSection from './components/ColorPaletteSection';
import DownloadUploadSection from './components/DownloadUploadSection';
import PageLayoutSection from './components/PageLayoutSection';
import SectionHeader from './components/SectionHeader';
import TableStyleForm from './components/TableStyleForm';
import TitlePageSection from './components/TitlePageSection';
import TypographySection from './components/TypographySection';
import {
  useBrandingDescription,
  useDownloadDeployedTitlePage,
  useDownloadExportAssets,
  useDownloadTitlePageTemplate,
  useExportBranding,
  useGenerateExportAssets,
  useParseDocxBranding,
  usePreviewExportAssets,
  useUpdateExportBranding,
  useUploadExportAssetsFile,
  useUploadLogo,
  useUploadTitlePage,
} from './hooks/useExportBranding';
import type { ExportBrandingConfigData } from './types';
import { DEFAULT_CONFIG } from './types';

type Props = {
  company: Company;
};

export default function ExportAssetsContent({ company }: Props) {
  const { branding, isLoading, isError, refetch } = useExportBranding(company.id);
  const { updateBranding, isLoading: isSaving } = useUpdateExportBranding(company.id);
  const { generate, isLoading: isGenerating } = useGenerateExportAssets(company.id);
  const { download, isLoading: isDownloading } = useDownloadExportAssets(company.id);
  const { preview, isLoading: isPreviewing } = usePreviewExportAssets(company.id);
  const { uploadLogo } = useUploadLogo(company.id);
  const { uploadFile, isLoading: isUploading } = useUploadExportAssetsFile(company.id);
  const { parseDocx, isLoading: isParsing } = useParseDocxBranding(company.id);
  const { fetchDescription, isLoading: isFetchingDescription } = useBrandingDescription(company.id);
  const { downloadTemplate, isLoading: isDownloadingTemplate } = useDownloadTitlePageTemplate(
    company.id,
  );
  const { uploadFile: uploadTitlePage, isLoading: isUploadingTitlePage } = useUploadTitlePage(
    company.id,
  );
  const { download: downloadDeployedTitlePage, isLoading: isDownloadingDeployedTitlePage } =
    useDownloadDeployedTitlePage(company.id);

  const [localConfig, setLocalConfig] = useState<ExportBrandingConfigData | null>(null);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [descriptionOpened, { open: openDescription, close: closeDescription }] =
    useDisclosure(false);
  const [descriptionText, setDescriptionText] = useState('');

  const config = localConfig ?? branding?.config ?? DEFAULT_CONFIG;

  const hasUnsavedChanges = localConfig !== null;

  if (isLoading) {
    return <PageLoader />;
  }

  if (isError) {
    return (
      <Alert color="red" variant="light" title="Error">
        Failed to load branding configuration. Please try again.
      </Alert>
    );
  }

  const handleSave = async () => {
    try {
      await updateBranding(config);
      setLocalConfig(null);
      await refetch();
      notifications.show({
        title: 'Saved',
        message: 'Branding configuration saved.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save configuration.',
        color: 'red',
      });
    }
  };

  const handleGenerateConfirmed = async () => {
    closeConfirm();
    try {
      if (hasUnsavedChanges) {
        await updateBranding(config);
        setLocalConfig(null);
      }
      const result = await generate();
      const downloadUrl = result?.data?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
      await refetch();
      notifications.show({
        title: 'Generated',
        message: 'export_assets.docx has been generated and deployed.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate export assets.',
        color: 'red',
      });
    }
  };

  const handlePreviewDownload = async () => {
    try {
      if (hasUnsavedChanges) {
        await updateBranding(config);
        setLocalConfig(null);
        await refetch();
      }
      await preview();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate preview.',
        color: 'red',
      });
    }
  };

  const handleDeployedDownload = async () => {
    try {
      const result = await download();
      const downloadUrl = result?.data?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to download. The file may not exist yet for this tenant.',
        color: 'red',
      });
    }
  };

  const handleUpload = async (file: File) => {
    await uploadFile(file);
    await refetch();
  };

  const handleParseDocx = async (file: File) => {
    await parseDocx(file);
    setLocalConfig(null);
    await refetch();
  };

  const handleLogoUpload = async (logoType: string, file: File) => {
    await uploadLogo(logoType, file);
    await refetch();
  };

  const handleDownloadTitlePageTemplate = async () => {
    try {
      await downloadTemplate();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate title page template.',
        color: 'red',
      });
    }
  };

  const handleUploadTitlePage = async (file: File) => {
    await uploadTitlePage(file);
  };

  const handleDownloadDeployedTitlePage = async () => {
    try {
      const result = await downloadDeployedTitlePage();
      const downloadUrl = result?.data?.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
      }
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to download. The title page may not exist yet for this tenant.',
        color: 'red',
      });
    }
  };

  const handleViewDescription = async () => {
    try {
      const result = await fetchDescription();
      setDescriptionText(result?.data?.description ?? '');
      openDescription();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to fetch branding description.',
        color: 'red',
      });
    }
  };

  const handleChange = (updated: ExportBrandingConfigData) => {
    setLocalConfig(updated);
  };

  return (
    <Stack gap="xxxl" maw={900} pb="xxxxl" p="md" bg="white">
      <Box pos="sticky" top={0} style={{ zIndex: 10 }} py="md" bg="white">
        <Group justify="space-between" align="center">
          <Box>
            <Text size="displayXs" fw={FONT_WEIGHTS.semibold}>
              Export Branding
            </Text>
            <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.6">
              {company.name} — {company.tenantName}
            </Text>
          </Box>
          <Group gap="sm">
            <Button
              onClick={handleViewDescription}
              loading={isFetchingDescription}
              variant="light"
              color="gray"
            >
              View Description
            </Button>
            <Button
              onClick={handleSave}
              loading={isSaving}
              disabled={!hasUnsavedChanges}
              variant="light"
            >
              Save
            </Button>
            <Button onClick={openConfirm} loading={isGenerating}>
              Generate & Deploy
            </Button>
          </Group>
        </Group>
      </Box>

      <ColorPaletteSection config={config} onChange={handleChange} />

      <TypographySection config={config} onChange={handleChange} />

      <Box>
        <SectionHeader
          title="Table Style"
          subtitle="Configure the AfternoonTable style used for all exported tables."
        />
        <TableStyleForm config={config} onChange={handleChange} />
      </Box>

      <Box>
        <SectionHeader
          title="Bullets"
          subtitle="Choose the bullet character for each indentation level."
        />
        <BulletStylesForm config={config} onChange={handleChange} />
      </Box>

      <PageLayoutSection
        config={config}
        onChange={handleChange}
        onLogoUpload={handleLogoUpload}
        firstPageLogoKey={branding?.firstPageHeaderLogoS3Key ?? null}
        subsequentPageLogoKey={branding?.subsequentPageHeaderLogoS3Key ?? null}
        footerLogoKey={branding?.footerLogoS3Key ?? null}
      />

      <Box>
        <SectionHeader
          title="Title Page"
          subtitle="Generate a title page template with Jinja tags, customise it in Word, and upload the final version."
        />
        {branding?.hasExportAssets ? (
          <TitlePageSection
            onDownloadTemplate={handleDownloadTitlePageTemplate}
            isDownloadingTemplate={isDownloadingTemplate}
            onUpload={handleUploadTitlePage}
            isUploading={isUploadingTitlePage}
            onDeployedDownload={handleDownloadDeployedTitlePage}
            isDeployedDownloading={isDownloadingDeployedTitlePage}
          />
        ) : (
          <Alert color="gray" variant="light">
            Deploy export assets first using Generate & Deploy above before configuring the title
            page.
          </Alert>
        )}
      </Box>

      <Box>
        <SectionHeader
          title="Download & Upload"
          subtitle="Preview, download, or manually override the deployed export_assets.docx."
        />
        <DownloadUploadSection
          onPreviewDownload={handlePreviewDownload}
          isPreviewDownloading={isPreviewing}
          onDeployedDownload={handleDeployedDownload}
          isDeployedDownloading={isDownloading}
          onUpload={handleUpload}
          isUploading={isUploading}
          onParseDocx={handleParseDocx}
          isParsing={isParsing}
        />
      </Box>

      <Box>
        <BotImageUpload companyId={company.id} />
      </Box>

      <Modal
        opened={confirmOpened}
        onClose={closeConfirm}
        title="Overwrite export_assets.docx?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will generate a new export_assets.docx and overwrite the currently deployed file
            for{' '}
            <Text span fw={FONT_WEIGHTS.semibold}>
              {company.tenantName}
            </Text>
            . This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button color="red" onClick={handleGenerateConfirmed}>
              Generate & Deploy
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={descriptionOpened}
        onClose={closeDescription}
        title="Branding Description"
        centered
        size="lg"
      >
        <ScrollArea h={500}>
          <Code block>{descriptionText}</Code>
        </ScrollArea>
      </Modal>
    </Stack>
  );
}
